import { computed } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { MenuImportsService } from '@/api/services/menu-imports.service';
import { rowDecorations } from '@/domain/menu-import/proposal-effects';
import type { TRowDecoration } from '@/domain/menu-import/proposal-effects';
import type {
  IDraftDelta,
  IMenuImportTurn,
  IRetryOfDto,
  TMenuProposalStatus,
} from '@/interfaces/IMenuImport';
import type { IMenuImportSession } from './menu-import-context';

export interface IUseMenuImportTurns {
  /** The session's chat turns, oldest first. */
  turns: Readonly<Ref<IMenuImportTurn[]>>;
  /** A turn is waiting for the assistant's answer. */
  thinking: ComputedRef<boolean>;
  /** Dish id → mark of the latest done turn's pending proposals. */
  decorations: ComputedRef<Record<string, TRowDecoration>>;
  /** 202: the turn is added as `thinking`; null (with a toast) when it was refused. */
  send(message: string, selection: string[]): Promise<IMenuImportTurn | null>;
  /** Through the session's serial queue; the proposal reads `applied` at once. */
  apply(turnId: string, proposalId: string): Promise<IDraftDelta | null>;
  discard(turnId: string, proposalId: string): Promise<boolean>;
  /** «Probar otra vez»: the same message and selection, telling the assistant which proposal was rejected. */
  retry(turnId: string, proposalId: string): Promise<IMenuImportTurn | null>;
}

/** The turn with one proposal in another status (a new object: the list is replaced, never mutated). */
function withProposalStatus(
  turn: IMenuImportTurn,
  proposalId: string,
  status: TMenuProposalStatus
): IMenuImportTurn {
  return {
    ...turn,
    proposals: turn.proposals.map((proposal) =>
      proposal.proposalId === proposalId ? { ...proposal, status } : proposal
    ),
  };
}

/**
 * «Pedir cambios» (plan §4.6, §10.4, D18): chat turns and their proposals.
 * The assistant answers asynchronously (socket `turn` events, or the
 * session's polling while a turn thinks); nothing auto-applies.
 */
export function useMenuImportTurns(
  session: IMenuImportSession
): IUseMenuImportTurns {
  const thinking = computed(() =>
    session.turns.value.some((turn) => turn.status === 'thinking')
  );
  const decorations = computed(() => rowDecorations(session.turns.value));

  function findTurn(turnId: string): IMenuImportTurn | null {
    return session.turns.value.find((turn) => turn.turnId === turnId) ?? null;
  }

  function markProposal(
    turnId: string,
    proposalId: string,
    status: TMenuProposalStatus
  ): void {
    const turn = findTurn(turnId);
    if (turn) session.upsertTurn(withProposalStatus(turn, proposalId, status));
  }

  async function sendTurn(
    message: string,
    selection: string[],
    retryOf?: IRetryOfDto
  ): Promise<IMenuImportTurn | null> {
    const sid = session.sessionId.value;
    const text = message.trim();
    if (!sid || !text || thinking.value) return null;
    const turn = await session.run(
      () =>
        MenuImportsService.sendTurn(sid, {
          clientTurnId: uuidv4(),
          message: text,
          selection,
          ...(retryOf ? { retryOf } : {}),
        }),
      'menuImport.errors.generic'
    );
    if (!turn || session.sessionId.value !== sid) return null;
    session.upsertTurn(turn);
    return turn;
  }

  function send(
    message: string,
    selection: string[]
  ): Promise<IMenuImportTurn | null> {
    return sendTurn(message, selection);
  }

  async function apply(
    turnId: string,
    proposalId: string
  ): Promise<IDraftDelta | null> {
    const delta = await session.apply(turnId, proposalId);
    if (delta) markProposal(turnId, proposalId, 'applied');
    return delta;
  }

  async function discard(turnId: string, proposalId: string): Promise<boolean> {
    const sid = session.sessionId.value;
    if (!sid) return false;
    const done = await session.run(async () => {
      await MenuImportsService.discardProposal(sid, turnId, proposalId);
      return true;
    }, 'menuImport.errors.generic');
    if (!done || session.sessionId.value !== sid) return false;
    markProposal(turnId, proposalId, 'discarded');
    return true;
  }

  async function retry(
    turnId: string,
    proposalId: string
  ): Promise<IMenuImportTurn | null> {
    const turn = findTurn(turnId);
    if (!turn) return null;
    const next = await sendTurn(turn.message, turn.selection, {
      turnId,
      proposalId,
    });
    // The server discards the rejected proposal when it accepts the new turn.
    if (next) markProposal(turnId, proposalId, 'discarded');
    return next;
  }

  return {
    turns: session.turns,
    thinking,
    decorations,
    send,
    apply,
    discard,
    retry,
  };
}
